// trace-viewer/src/export/triplanarPolicy.ts
//
// WHICH materials project, and at what tuning — the policy half of triplanar,
// with no shader in sight. It lives under export/ because projection is
// ultimately an EXPORT decision: TraceExport's bake (stack PR #677) turns
// these configs into real UVs, and the editor's live preview
// (editor/TriplanarMaterial.ts, which imports this module) exists to show
// exactly what that bake will do. Keeping
// the policy here — and the plugin's live state readable through Babylon's
// own plugin manager rather than the plugin class — is what lets the export
// pipeline run without importing editor code, so the runtime bundle (which
// carries TraceExport via TraceViewer) stays free of the preview shader.
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Material } from "@babylonjs/core/Materials/material";
import type { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";

/** How a material should be projected. From the resolvers, `null` means "nothing to project". */
export interface TriplanarConfig {
    /** Texture repetitions per meter. Higher = smaller pattern. */
    tileSize: number;
    /** Blend exponent between the three axis projections. Higher = sharper seams. */
    sharpness: number;
}

/**
 * Per-family projection TUNING, carried over from the prototype
 * (`TriplanarDefaults` in its StyleSystem). This table used to double as the
 * projection GATE — only named families projected — until a flat-colored
 * material carrying only a normal map smeared on a stretched component:
 * every projectable map stretches with the geometry, not just the
 * directional-albedo families. Now ANY material with a projectable texture
 * projects (see {@link triplanarConfigForMaterial}); a family match merely
 * tunes tile density, and everything else takes
 * {@link DEFAULT_TRIPLANAR_CONFIG}.
 *
 * Matched on the material's source filename because that is what the library
 * names carry ("Fabric Canvas.glb", "Concrete Wall.glb").
 */
const FAMILIES: { match: RegExp; config: TriplanarConfig }[] = [
    { match: /wood|plank|bark|timber|parquet/i, config: { tileSize: 2.0, sharpness: 2.0 } },
    { match: /fabric|carpet|denim|knit|linen|canvas|basket|cork/i, config: { tileSize: 3.0, sharpness: 2.5 } },
    { match: /stone|marble|granite|slate|terrazzo|gravel/i, config: { tileSize: 1.5, sharpness: 2.0 } },
    // "Conrete" is a real spelling in the library — matched deliberately.
    { match: /concrete|conrete|cardboard/i, config: { tileSize: 1.0, sharpness: 2.0 } },
    // The tile and roof terms are word-bounded on the left (relies on
    // triplanarConfigFor's camelCase AND underscore splits — bare substring
    // matching pulled "Textile"/"Projectile" into this family, and _ is a \w
    // character, so without the underscore split \b never fires in
    // "M_Tile_01_A") and letter-bounded on the right ("Tileable" must not
    // ride in on "tile", "Waterproof"/"Fireproof" must not ride in on
    // "roof"). Brick containments ("Brickwork") are semantically fine.
    { match: /brick|\broofs?(?![a-z])|\btiles?(?![a-z])/i, config: { tileSize: 2.5, sharpness: 2.0 } },
];

/**
 * The FAMILY tuning for a name, or null when no family matches. Null no
 * longer means "UV mapping is fine" — projection is gated on the material's
 * textures, not its name (see {@link triplanarConfigForMaterial}).
 *
 * Takes every name that might identify the material and matches on any of
 * them, because neither signal is reliable alone. A CMS-hosted GLB has a GUID
 * filename ("4e038788-….glb") and only its internal material name carries
 * meaning ("M_HexBricks_01_A"); a staged local file is the reverse when its
 * material is mislabelled — the library ships Chrome as "M_Bronze_01_A".
 */
export function triplanarConfigFor(...names: (string | undefined)[]): TriplanarConfig | null {
    for (const raw of names) {
        if (!raw) continue;
        // Lenient decode: a local filename with a literal % ("50%Wool.glb")
        // is not percent-encoded — URIError here must not break the caller's
        // whole attach, the raw name is still a usable match target.
        let decoded = raw;
        try { decoded = decodeURIComponent(raw); } catch { /* not URI-encoded */ }
        const name = decoded.split("/").pop() ?? decoded;
        // Split camelCase and underscores before matching so word-bounded
        // family terms can't land inside larger words ("Textile"/"Projectile"
        // vs "CeramicTile") yet still fire between library separators —
        // "M_Tile_01_A" has no case edge and _ is \w, so \b alone sees none.
        const spaced = name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " ");
        const hit = FAMILIES.find(f => f.match.test(spaced));
        if (hit) return hit.config;
    }
    return null;
}

/**
 * Fallback tuning for a projectable material outside every family: one
 * texture repetition per meter, standard blend sharpness. Deliberately a
 * placeholder heuristic — asset-authored projection tags will replace the
 * whole name-based scheme (Martin's direction on #646); once assets carry
 * their own tuning, the family table above becomes legacy defaults for
 * untagged materials and this constant the last resort.
 */
export const DEFAULT_TRIPLANAR_CONFIG: TriplanarConfig = Object.freeze({ tileSize: 1.0, sharpness: 2.0 });

/** Does this material carry any texture the projection can re-map? */
function hasProjectableTexture(material: PBRMaterial): boolean {
    return !!(material.albedoTexture || material.bumpTexture || material.metallicTexture);
}

/**
 * The projection config for a concrete MATERIAL — the resolver behind both
 * the live preview shader and the export-time UV bake.
 *
 * Any PBR material carrying any projectable texture (albedo, bump/normal,
 * or metallic/ORM) projects: the counterexample that killed the old
 * albedo+family gate was a flat-colored material whose NORMAL map smeared
 * on a stretched component — relief and shine stretch exactly like color
 * does. Family names no longer gate, they only tune tile density; a
 * textured material outside every family gets
 * {@link DEFAULT_TRIPLANAR_CONFIG}.
 *
 * Null when there is nothing to project: no projectable texture (a flat
 * colour alone cannot stretch), or not a PBRMaterial (the plugin injects
 * into the PBR fragment chain). `applyTriplanar` (editor side) declines
 * exactly the same materials, so callers can gate clones and bakes on this
 * one result.
 *
 * `names` feed family tuning via {@link triplanarConfigFor} — pass every
 * name that might identify the material (source URL, GLB material name).
 */
export function triplanarConfigForMaterial(
    material: Material | null | undefined,
    ...names: (string | undefined)[]
): TriplanarConfig | null {
    if (!material || material.getClassName() !== "PBRMaterial") return null;
    if (!hasProjectableTexture(material as PBRMaterial)) return null;
    return triplanarConfigFor(...names) ?? DEFAULT_TRIPLANAR_CONFIG;
}

/**
 * The asset-authored projection policy for a material, read from its glTF
 * extras — Martin's #646 direction: policy is AUTHORED INTO ASSETS, consumed
 * at edit/export time, and the runtime loader reads nothing.
 *
 * Authoring shape (glTF material extras, landed on the Babylon material by
 * the loader's ExtrasAsMetadata extension as `metadata.gltf.extras`):
 *
 *     "extras": { "trace": { "triplanar": { "tileSize": 2, "sharpness": 2 } } }
 *     "extras": { "trace": { "triplanar": false } }   // explicit opt-OUT
 *     "extras": { "trace": { "triplanar": true } }    // opt-in at family/default tuning
 *
 * Returns:
 * - a {@link TriplanarConfig} when the asset authors one (`true` defers the
 *   numbers to family tuning via `names`, else the default config);
 * - `false` when the asset explicitly opts out (beats every other source —
 *   a live preview plugin included);
 * - `null` when the asset says nothing (callers fall through to the next
 *   step of their chain: live plugin, then the name/texture heuristic).
 *
 * Unusable numbers in a tag are treated field-wise as unauthored (default
 * applied) — a half-written tag must not bake NaN UVs. "Unusable" differs per
 * field: `sharpness: 0` is a real authored value (equal-thirds blend, no
 * sharpening) and is honoured, while `tileSize: 0` is not a tuning but a
 * degenerate mapping — zero repetitions per meter collapses every UV onto one
 * texel — so it falls back like a negative would.
 */
export function readTriplanarTag(
    material: Material | null | undefined,
    ...names: (string | undefined)[]
): TriplanarConfig | false | null {
    const metadata = material?.metadata as { gltf?: { extras?: { trace?: { triplanar?: unknown } } } } | null | undefined;
    const tag = metadata?.gltf?.extras?.trace?.triplanar;
    if (tag === undefined || tag === null) return null;
    if (tag === false) return false;
    const fallback = triplanarConfigFor(...names, material?.name) ?? DEFAULT_TRIPLANAR_CONFIG;
    if (tag === true) return fallback;
    if (typeof tag !== "object") return null; // unrecognized tag shape — say nothing
    const raw = tag as { tileSize?: unknown; sharpness?: unknown };
    const num = (value: unknown, fall: number, min: number): number =>
        typeof value === "number" && Number.isFinite(value) && value >= min ? value : fall;
    return {
        tileSize: num(raw.tileSize, fallback.tileSize, Number.MIN_VALUE),
        sharpness: num(raw.sharpness, fallback.sharpness, 0),
    };
}

/**
 * The edit/export-time projection POLICY for a material — one resolver so the
 * live preview and the export bake cannot disagree:
 *
 *   1. asset-authored tag ({@link readTriplanarTag}); `false` opts out;
 *   2. else the name/texture heuristic ({@link triplanarConfigForMaterial}) —
 *      the documented TRANSITIONAL fallback until the library is tagged.
 *
 * Null means "leave the material on its authored UVs". Like the heuristic, a
 * tagged config only applies when the material actually carries a projectable
 * texture — with nothing sampled, nothing can smear.
 */
export function resolveTriplanarPolicy(
    material: Material | null | undefined,
    ...names: (string | undefined)[]
): TriplanarConfig | null {
    const tag = readTriplanarTag(material, ...names);
    if (tag === false) return null;
    if (tag) {
        if (!material || material.getClassName() !== "PBRMaterial") return null;
        return hasProjectableTexture(material as PBRMaterial) ? tag : null;
    }
    return triplanarConfigForMaterial(material, ...names);
}

/**
 * The EXPORT BAKE's projection policy for one material: the same chain as
 * {@link resolveTriplanarPolicy} with the live editor preview spliced in
 * between the tag and the heuristic, plus the anchor node the projection is
 * measured against.
 *
 *  (a) ASSET-AUTHORED TAG — an explicit `false` opts out entirely (it beats a
 *      live preview plugin); a config projects at the authored numbers,
 *      anchored to `objectRoot`, the same node the editor preview anchors to,
 *      so a tagged asset bakes IDENTICALLY in an editor session and a
 *      headless one.
 *  (b) else the LIVE editor preview plugin, if one is enabled with at least
 *      one projected map, at ITS anchor.
 *  (c) else the textured-material heuristic — the documented TRANSITIONAL
 *      fallback until the library is tagged.
 *
 * It lives here, beside the preview's resolver, because both rungs (a) and
 * (c) are shared: a copy in the exporter is how preview and bake drift apart.
 *
 * `names` feeds family tuning and must carry every name that identifies the
 * material — its own name AND the recorded original's (a Renderer tint clone
 * renames the material, and tuning by the clone's name silently bakes DEFAULT
 * density where the preview used the library family).
 */
export function resolveTriplanarForBake(
    material: Material | null | undefined,
    objectRoot: TransformNode,
    ...names: (string | undefined)[]
): { config: TriplanarConfig; anchor: TransformNode | null } | null {
    if (!material) return null;
    // Live preview state, read through Babylon's plugin manager — never the
    // editor plugin class, so this module stays out of the editor's import
    // graph (and the runtime bundle stays free of the preview shader).
    const live = getLiveTriplanar(material);
    const tag = readTriplanarTag(material, ...names);
    if (tag === false) return null;
    if (tag) {
        // A tag still needs something to sample. Under NullEngine test
        // degradation the material's own slots are nulled (so the glTF
        // serializer cannot hang dumping images) and the live plugin is the
        // only carrier left — hence the `|| live`.
        const projectable = material.getClassName() === "PBRMaterial"
            && hasProjectableTexture(material as PBRMaterial);
        if (!projectable && !live) return null;
        return { config: tag, anchor: objectRoot };
    }
    if (live) {
        return {
            config: { tileSize: live.tileSize, sharpness: live.sharpness },
            anchor: live.anchorNode,
        };
    }
    const heuristic = triplanarConfigForMaterial(material, ...names);
    return heuristic ? { config: heuristic, anchor: objectRoot } : null;
}

// ── Live preview state, read without the plugin class ────────────────────

/**
 * The editor plugin's registered name in Babylon's per-material plugin
 * manager — the lookup key {@link getLiveTriplanar} reads live state through,
 * and the single string the plugin class (editor side) registers under.
 */
export const TRIPLANAR_PLUGIN_NAME = "TriplanarPBRPlugin";

/**
 * Live projection state of an editor preview plugin — the structural slice of
 * the plugin the export bake needs. Reading it through this shape (via
 * Babylon's `material.pluginManager`) instead of the plugin class is what
 * keeps editor code out of the export pipeline's import graph.
 */
export interface LiveTriplanarState {
    tileSize: number;
    sharpness: number;
    anchorNode: TransformNode | null;
}

/**
 * The ACTIVE editor-preview projection on a material, if any: enabled, with
 * at least one map to project. Null on materials with no plugin manager (the
 * common case — managers exist only once a plugin attaches), a disabled
 * plugin, or a plugin with nothing sampled. This reads Babylon's own plugin
 * registry, so it also sees plugin instances that `Material.clone()`
 * recreated through `_ParsePlugins`.
 */
// rigidFrameInverseToRef scratch — module-private, never escapes.
const RF_SCALE = new Vector3();
const RF_ROT = new Quaternion();
const RF_POS = new Vector3();
const RF_FRAME = Matrix.Identity();

/**
 * Inverse of `node`'s RIGID world frame — rotation + translation only, scale
 * excluded so projection coordinates keep world metric (a tile is a tile in
 * meters; a scaled or morphed axis re-tiles instead of carrying the pattern
 * with it). This is THE agreement point between the editor preview shader
 * (bindForSubMesh's triInvAnchor uniform) and the export bake's projMatrix:
 * both sides call this one helper, so they cannot decompose the anchor
 * differently. Writes the inverse into `out`; when `outFrame` is given, the
 * un-inverted rigid frame is written there too (the shader binds both).
 */
export function rigidFrameInverseToRef(node: TransformNode, out: Matrix, outFrame?: Matrix): void {
    node.getWorldMatrix().decompose(RF_SCALE, RF_ROT, RF_POS);
    const frame = outFrame ?? RF_FRAME;
    Matrix.ComposeToRef(Vector3.OneReadOnly, RF_ROT, RF_POS, frame);
    frame.invertToRef(out);
}

// projectionFrameInverseToRef scratch — module-private, never escapes.
const PF_ANIM = Matrix.Identity();
const PF_UNDO = Matrix.Identity();

/**
 * {@link rigidFrameInverseToRef} for a mesh an entrance or exit is moving.
 * `animationRoot` is the node the AnimationSequence drives between `anchor`
 * and the mesh (SceneObject.getOrCreateAnimationRoot); its motion is undone
 * first, so a fragment samples where its vertex sits at REST in the anchor's
 * rigid frame — the coordinate the export bake wrote into the UVs. Without
 * this the projection field stood still while the mesh spun and grew through
 * it, and a baked component only looked painted once its clip ended. With no
 * animation root, or one that is the anchor itself, this is exactly
 * rigidFrameInverseToRef.
 */
export function projectionFrameInverseToRef(
    anchor: TransformNode,
    animationRoot: TransformNode | null | undefined,
    out: Matrix,
    outFrame?: Matrix,
): void {
    rigidFrameInverseToRef(anchor, out, outFrame);
    if (!animationRoot || animationRoot === anchor || animationRoot.isDisposed()) return;
    // A point at p under the animation root sits at p·Wanim now and at
    // p·Wanchor at rest (row-vector order): world → rest world → rigid frame.
    animationRoot.getWorldMatrix().invertToRef(PF_ANIM);
    PF_ANIM.multiplyToRef(anchor.getWorldMatrix(), PF_UNDO);
    PF_UNDO.multiplyToRef(out, out);
    if (outFrame) out.invertToRef(outFrame);
}

export function getLiveTriplanar(material: Material | null | undefined): LiveTriplanarState | null {
    const plugin = material?.pluginManager?.getPlugin<{
        isEnabled: boolean;
        texture: unknown;
        normalTexture: unknown;
        ormTexture: unknown;
        tileSize: number;
        sharpness: number;
        anchorNode: TransformNode | null;
    }>(TRIPLANAR_PLUGIN_NAME);
    if (!plugin?.isEnabled) return null;
    if (!plugin.texture && !plugin.normalTexture && !plugin.ormTexture) return null;
    return {
        tileSize: plugin.tileSize,
        sharpness: plugin.sharpness,
        anchorNode: plugin.anchorNode ?? null,
    };
}
